import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';

export default function Portfolios() {
  const { t } = useTranslation();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_ar: '', description: '', description_ar: '' });

  useEffect(() => {
    fetchPortfolios();
  }, []);

  const fetchPortfolios = async () => {
    try {
      const response = await api.getPortfolios();
      setPortfolios(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPortfolio) {
        await api.updatePortfolio(editingPortfolio.id, formData);
      } else {
        await api.createPortfolio(formData);
      }
      setDialogOpen(false);
      setFormData({ name: '', name_ar: '', description: '', description_ar: '' });
      setEditingPortfolio(null);
      fetchPortfolios();
    } catch (error) {
      console.error('Failed to save portfolio:', error);
    }
  };

  const handleEdit = (portfolio) => {
    setEditingPortfolio(portfolio);
    setFormData({
      name: portfolio.name,
      name_ar: portfolio.name_ar || '',
      description: portfolio.description || '',
      description_ar: portfolio.description_ar || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this portfolio?')) {
      try {
        await api.deletePortfolio(id);
        fetchPortfolios();
      } catch (error) {
        console.error('Failed to delete portfolio:', error);
      }
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('portfolios')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingPortfolio(null); setFormData({ name: '', name_ar: '', description: '', description_ar: '' }); }}>
                <Plus className="mr-2 h-4 w-4" /> {t('addPortfolio')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingPortfolio ? t('edit') : t('add')} {t('portfolios')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('portfolioName')} (EN)</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <Label>{t('portfolioName')} (AR)</Label>
                  <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} />
                </div>
                <div>
                  <Label>{t('description')} (EN)</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>
                <div>
                  <Label>{t('description')} (AR)</Label>
                  <Input value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} />
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('portfolios')} ({portfolios.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : portfolios.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No portfolios yet. Create your first portfolio!</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('portfolioName')}</TableHead>
                    <TableHead>{t('description')}</TableHead>
                    <TableHead>{t('totalProperties')}</TableHead>
                    <TableHead>{t('totalUnits')}</TableHead>
                    <TableHead>{t('occupancyRate')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portfolios.map((portfolio) => (
                    <TableRow key={portfolio.id}>
                      <TableCell className="font-medium">{portfolio.name}</TableCell>
                      <TableCell>{portfolio.description || '-'}</TableCell>
                      <TableCell>{portfolio.total_properties}</TableCell>
                      <TableCell>{portfolio.total_units}</TableCell>
                      <TableCell>{portfolio.occupancy_rate}%</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(portfolio)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(portfolio.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}